import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Stato dell'account di un cliente, per email:
 *  - 'none'    → mai visto, va creato
 *  - 'invited' → anagrafato (es. alla creazione della travel request) ma mai
 *                entrato: gli servono ancora le credenziali di primo accesso
 *  - 'active'  → ha già fatto accesso alla piattaforma
 *
 * Sostituisce useCheckUserExists: sapere solo se l'email esiste non basta,
 * perché un cliente anagrafato dal TD "esiste" pur non potendo accedere.
 */
export type ClientAccountState = 'none' | 'invited' | 'active';

export const useClientAccountState = (email: string | null | undefined) => {
  const [state, setState] = useState<ClientAccountState>('none');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkState = async () => {
      if (!email) {
        setState('none');
        return;
      }

      setIsLoading(true);
      try {
        // RPC SECURITY DEFINER: aggira le policy che impediscono al TD di
        // vedere i traveler senza TR assegnate, e non espone dati dell'utente.
        const { data, error } = await supabase
          // `as any`: la RPC entra in types.ts solo alla prossima rigenerazione.
          .rpc('get_client_account_state' as any, {
            p_email: email.toLowerCase().trim()
          });

        if (error) {
          console.error('Error checking client account state:', error);
          setState('none');
        } else {
          setState((data as ClientAccountState) ?? 'none');
        }
      } catch (err) {
        console.error('Error checking client account state:', err);
        setState('none');
      } finally {
        setIsLoading(false);
      }
    };

    checkState();
  }, [email]);

  return { state, isLoading };
};
